import { CommentPopover } from "./CommentPopover";
import { LocationButton } from "./LocationButton";
import { RouteSelector } from "./RouteSelector";
import { ShippingAddressSelectorCell } from "./ShippingAddressSelectorCell";
import { PendingFieldOverlay } from "./PendingFieldOverlay";
import { usePreOrderFeatureFlags } from "../../hooks/usePreOrderFeatureFlags";
import type { PreOrder } from "@/app/api/types/preOrder";

interface PreOrderControlsProps {
  preOrder: PreOrder;
  onMutate: (data: Record<string, any>) => void;
  /** Disable all controls */
  disabled?: boolean;
  /**
   * Optional reader from the host's `usePendingHeaderFields()` — when present,
   * each control wraps in a `PendingFieldOverlay` so users see an inline
   * spinner while the mutation is in flight (Dexie write → SSE fan-out is
   * ~500 ms, otherwise the cell looks frozen). Pass `currentValue` so the
   * spinner persists until the rendered prop has caught up with the write.
   * Omit for read-only contexts.
   */
  isFieldPending?: (field: string, currentValue?: unknown) => boolean;
  /** Comment popover props */
  commentProps?: {
    /** Whether the comment fills remaining space */
    fill?: boolean;
    iconOnly?: boolean;
    /** Hide the comment (rendered externally) */
    hidden?: boolean;
    triggerClassName?: string;
  };
}

/**
 * Shared controls for pre-order headers: shipping address, location, route,
 * and comment. Used by both PreOrderHeader and PreOrderContent.
 *
 * The shipping-address picker is gated by `pickDelivery` and binds
 * `shipping_address_id`. The cell fetches partner addresses itself from
 * the offline-first store, so callers only pass the pre-order + onMutate.
 */
export function PreOrderControls({
  preOrder,
  onMutate,
  disabled = false,
  isFieldPending,
  commentProps,
}: PreOrderControlsProps) {
  const {
    isVivoMode,
    pickDeliveryEnabled,
    pickLocationEnabled,
    pickRouteEnabled,
  } = usePreOrderFeatureFlags();

  const wrap = (field: string, currentValue: unknown, node: React.ReactNode) =>
    isFieldPending ? (
      <PendingFieldOverlay isPending={isFieldPending(field, currentValue)}>
        {node}
      </PendingFieldOverlay>
    ) : (
      node
    );

  return (
    <>
      {/* Shipping address (Partner address, binds shipping_address_id) */}
      {pickDeliveryEnabled && (
        <div
          className={`flex-shrink-0 ${disabled ? "pointer-events-none opacity-50" : ""}`}
        >
          {wrap(
            "shipping_address_id",
            preOrder.shipping_address_id ?? null,
            <ShippingAddressSelectorCell
              partnerId={preOrder.partner_id ?? null}
              value={preOrder.shipping_address_id ?? null}
              onChange={(addressId) => {
                onMutate({ shipping_address_id: addressId });
              }}
              fallbackAddresses={preOrder.partner?.addresses}
            />,
          )}
        </div>
      )}

      {/* Location */}
      {pickLocationEnabled && (
        <div
          className={`flex-shrink-0 ${disabled ? "pointer-events-none opacity-50" : ""}`}
        >
          {wrap(
            "location_id",
            preOrder.location_id ?? null,
            <LocationButton
              value={preOrder.location_id || null}
              onChange={(locationId) => {
                onMutate({ location_id: locationId });
              }}
            />,
          )}
        </div>
      )}

      {/* Delivery route (Route entity, binds route_id) */}
      {pickRouteEnabled && (
        <div
          className={`flex-shrink-0 ${disabled ? "pointer-events-none opacity-50" : ""}`}
        >
          {wrap(
            "route_id",
            preOrder.route_id ?? null,
            <RouteSelector
              value={preOrder.route_id || null}
              onChange={(routeId) => {
                onMutate({ route_id: routeId });
              }}
            />,
          )}
        </div>
      )}

      {/* Comment */}
      {!commentProps?.hidden && (
        <div
          className={`${
            commentProps?.fill ? "flex-1 min-w-[80px]" : "flex-shrink-0"
          } ${disabled ? "pointer-events-none opacity-50" : ""}`}
        >
          {wrap(
            "comment",
            preOrder.comment ?? "",
            <CommentPopover
              value={preOrder.comment || ""}
              onChange={(value) => {
                onMutate({ comment: value });
              }}
              disabled={disabled}
              isVivoMode={isVivoMode}
              flowMetadata={preOrder.pre_order_metadata}
              onM3CommentsChange={(m3Comments) => {
                onMutate({
                  pre_order_metadata: {
                    ...(preOrder.pre_order_metadata || {}),
                    m3_comments: m3Comments,
                  },
                });
              }}
              iconOnly={commentProps?.iconOnly}
              triggerClassName={commentProps?.triggerClassName}
            />,
          )}
        </div>
      )}
    </>
  );
}
